import { ExportToCsv } from 'export-to-csv';
import { computed, observable } from 'mobx';

import { Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { makeTableQuery, TableQuery } from '../../adl-gen/common/tabular';
import { AppClient, Auth0SamlConnection, LearningProvider, Licence } from '../../adl-gen/totalinteraction/futurefit/db';
import {
  AppClientListEntry,
  Auth0Client,
  ClientParticipant,
  Entry,
  makeClientParticipant,
  RepositoryEntries,
  RepositoryEntrySet
} from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  CreateAuth0ClientReq,
  makeRepositoryPutReq,
  makeRepositoryReq,
  RepositoryPutEntry,
  RepositoryPutReq,
  RepositoryReq,
  UpdateAuth0ClientReq
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';

import { DataStore, TransportLayer } from './index';

class AppClientApi implements TransportLayer<AppClient> {
  kind: string = 'AppClient';
  constructor(protected readonly service: Service) { }
  create = async (item: AppClient): Promise<DbKey<AppClient>> => this.service.createAppClient(item);
  update = async (itemWithId: WithDbId<AppClient>): Promise<void> => this.service.updateAppClient(itemWithId);
  doDelete = async (id: DbKey<AppClient>): Promise<void> => this.service.deleteAppClient(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<AppClient>>> => this.service.queryAppClients(q);

  createWithGroup = async (item: AppClient): Promise<DbKey<AppClient>> => this.service.createAppClientWithGroup(item);

  queryList = async (q: TableQuery): Promise<Paginated<AppClientListEntry>> => this.service.queryAppClientList(q);
  queryLearningProviders = async (q: TableQuery): Promise<Paginated<WithDbId<LearningProvider>>> =>
    this.service.queryLearningProviders(q);
  querySSOConnections = async (q: TableQuery): Promise<Paginated<WithDbId<Auth0SamlConnection>>> =>
    this.service.queryAuth0SamlConnections(q);
  queryLicences = async (q: TableQuery): Promise<Paginated<WithDbId<Licence>>> => this.service.queryLicences(q);

  createAuth0Client = async (req: CreateAuth0ClientReq): Promise<Auth0Client> => this.service.createAuth0Client(req);
  updateAuth0Client = async (req: UpdateAuth0ClientReq): Promise<Auth0Client> => this.service.updateAuth0Client(req);
  deleteRepositoryEntry = async (req: RepositoryReq): Promise<void> => this.service.deleteRepositoryEntry(req);
  uploadRepositoryEntry = async (req: RepositoryPutReq): Promise<Entry> => this.service.uploadRepositoryEntry(req);
  listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> =>
    this.service.listRepositoryEntries(req);

  queryClientParticipantsReport = async (clientId: DbKey<AppClient>): Promise<Paginated<ClientParticipant>> =>
    this.service.queryClientParticipantsReport(clientId);
}

/// Lookup data
export interface AppClientData {
  appClient?: WithDbId<AppClient>;
  learningProviderList: Paginated<WithDbId<LearningProvider>>;
  ssoConnectionList: Paginated<WithDbId<Auth0SamlConnection>>;
  licenceList: Paginated<WithDbId<Licence>>;
}

export class AppClientStore extends DataStore<AppClient> {
  @observable.ref list?: Promise<Paginated<AppClientListEntry>>;
  @observable.ref repository: Promise<RepositoryEntrySet>;
  clientData: Promise<AppClientData>;

  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    id?: DbKey<AppClient>
  ): AppClientStore => {
    const store = new AppClientStore(new AppClientApi(service), identity, id);
    return store;
  };

  public constructor(transport: TransportLayer<AppClient>, identity: LoggedIn | LoggedInSuper, id?: DbKey<AppClient>) {
    super(transport, identity, id);
    if (this.isSuperUser) {
      this.list = this.initList();
    }
    // SuperUser uses the list of clients; everyone else must have only one, so this.data shouldn't really be used
    // // Don't autoload all data, we might only want a subset - hence not in base class
    // this.data = this.init(id);
    this.clientData = this.initData(id);
    /// Initiate repository with site-wide and Skillseeker-specific stuff
    this.repository = this.initRepository();
  }

  private readonly initList = (): Promise<Paginated<AppClientListEntry>> => {
    return this.loadList();
  };

  private readonly initData = async (id?: DbKey<AppClient>): Promise<AppClientData> => {
    if (this.authorized) {
      const clientData = this.loadClientData(id);
      return clientData;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly initRepository = async (): Promise<RepositoryEntrySet> => {
    const repository: RepositoryEntrySet = {};
    if (this.authorized) {
      return this.loadDefaultRepository(repository);
      // .then(repos => this.loadClientRepository(repos))
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadList = async (): Promise<Paginated<AppClientListEntry>> => {
    if (this.authorized) {
      const results = await this.queryList(this.queryAll);
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadClientData = async (id?: DbKey<AppClient>): Promise<AppClientData> => {
    const q = makeTableQuery({});
    // Use the base class method as it checks auth etc
    let appClient = id ? await this.setSelected(id) : undefined;
    const allLearningProviders = await this.queryLearningProviders(q);
    const allSSOConnections = await this.querySSOConnections(q);
    const allLicenceTypes = await this.queryLicences(q);
    return {
      appClient,
      learningProviderList: allLearningProviders,
      ssoConnectionList: allSSOConnections,
      licenceList: allLicenceTypes
    };
  };

  public loadClientRepository = async (
    repository: RepositoryEntrySet,
    clientDomain: string
  ): Promise<RepositoryEntrySet> => {
    if (this.authorized) {
      try {
        const clientPrefix = clientDomain;
        const clientIconsPath = `${clientPrefix}/icons/`;
        const clientSiteImagesPath = `${clientPrefix}/site-images/`;
        const clientIcons = await this.listRepositoryEntries(
          makeRepositoryReq({
            appClientId: this.identity.appClient.id,
            path: clientIconsPath
          })
        );
        const clientSiteImages = await this.listRepositoryEntries(
          makeRepositoryReq({
            appClientId: this.identity.appClient.id,
            path: clientSiteImagesPath
          })
        );
        return {
          ...repository,
          [clientIconsPath]: clientIcons,
          [clientSiteImagesPath]: clientSiteImages
        };
      } catch (e) {
        return Promise.reject(`Unable to load default repository: ${(e as Error).message}`);
      }
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadDefaultRepository = async (repository: RepositoryEntrySet): Promise<RepositoryEntrySet> => {
    try {
      // Always load skillseeker things because they can act as defaults
      const skillseekerIconsPath = 'skillseeker/icons/';
      const skillseekerSiteImagesPath = 'skillseeker/site-images/';

      const skillseekerIcons = await this.listRepositoryEntries(
        makeRepositoryReq({
          appClientId: this.identity.appClient.id,
          path: skillseekerIconsPath
        })
      );
      const skillseekerSiteImages = await this.listRepositoryEntries(
        makeRepositoryReq({
          appClientId: this.identity.appClient.id,
          path: skillseekerSiteImagesPath
        })
      );

      return {
        ...repository,
        [skillseekerIconsPath]: skillseekerIcons,
        [skillseekerSiteImagesPath]: skillseekerSiteImages
      };
    } catch (e) {
      return Promise.reject(`Unable to load default repository: ${(e as Error).message}`);
    }
  };

  private readonly queryList = async (q: TableQuery): Promise<Paginated<AppClientListEntry>> => {
    const result = await (this.transport as AppClientApi).queryList(q);
    return result;
  };

  private readonly queryLearningProviders = async (q: TableQuery): Promise<Paginated<WithDbId<LearningProvider>>> => {
    const result = await (this.transport as AppClientApi).queryLearningProviders(q);
    return result;
  };

  private readonly querySSOConnections = async (q: TableQuery): Promise<Paginated<WithDbId<Auth0SamlConnection>>> => {
    const result = await (this.transport as AppClientApi).querySSOConnections(q);
    return result;
  };

  private readonly queryLicences = async (q: TableQuery): Promise<Paginated<WithDbId<Licence>>> => {
    const result = await (this.transport as AppClientApi).queryLicences(q);
    return result;
  };

  @computed
  public get loadingList(): MaybeLoading<Paginated<AppClientListEntry>> {
    if (this.authorized) {
      if (this.list) {
        return fromPromise(this.list);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingData(): MaybeLoading<AppClientData> {
    if (this.authorized) {
      return fromPromise(this.clientData);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  public createWithGroup = async (item: AppClient): Promise<DbKey<AppClient>> => {
    if (this.authorized) {
      return (this.transport as AppClientApi).createWithGroup(item);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  /// Auth0 actions
  public createAuth0Client = async (req: CreateAuth0ClientReq): Promise<Auth0Client> => {
    if (this.authorized) {
      return (this.transport as AppClientApi).createAuth0Client(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  public updateAuth0Client = async (req: UpdateAuth0ClientReq): Promise<Auth0Client> => {
    if (this.authorized) {
      return (this.transport as AppClientApi).updateAuth0Client(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  /// S3 Actions
  public listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> => {
    if (this.authorized) {
      return (this.transport as AppClientApi).listRepositoryEntries(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  public uploadRepositoryEntry = async (req: RepositoryPutEntry): Promise<Entry> => {
    if (this.authorized) {
      return (this.transport as AppClientApi).uploadRepositoryEntry(
        makeRepositoryPutReq({
          appClientId: this.identity.appClient.id,
          entry: req
        })
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };

  // TODO: Currently unused
  _deleteRepositoryEntry = async (req: RepositoryReq): Promise<void> => {
    if (this.authorized) {
      return (this.transport as AppClientApi).deleteRepositoryEntry(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  @computed
  public get loadingRepository(): MaybeLoading<RepositoryEntrySet> {
    if (this.authorized) {
      return fromPromise(this.repository);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  // private readonly select = (id: DbKey<AppClient>): Promise<WithDbId<AppClient>> => {
  //   const q = makeTableQuery({
  //     filter: {
  //       kind: "equals",
  //       value: {
  //         field: "id",
  //         value: id
  //       }
  //     }
  //   })
  //   const client = this.query(q).then(resolve => {
  //     if (resolve.total_size !== 1) {
  //       return Promise.reject("AppClient not found/invalid query")
  //     }
  //     return resolve.items[0]
  //   })
  //   return client
  // }

  /// Report
  // TODO: currently unused
  _generateClientParticipantReport = async (client: WithDbId<AppClient>): Promise<void> => {
    if (this.authorizedForClient(client.id)) {
      const options = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        showTitle: true,
        title: `Participant Report - ${client.value.name}`,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true
        // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
      };

      const csvExporter = new ExportToCsv(options);

      // Fetch ye data
      const report: Paginated<ClientParticipant> = await (this.transport as AppClientApi).queryClientParticipantsReport(
        client.id
      );

      if (report.total_size > 0) {
        csvExporter.generateCsv(report.items);
      } else {
        const emptyItem: ClientParticipant = makeClientParticipant({
          userId: '',
          firstname: '',
          lastname: '',
          email: '',
          isManager: '',
          managerName: '',
          teamName: '',
          status: '',
          quizId: '',
          quizTitle: '',
          attempt: 0,
          completed: '',
          lastModified: '',
          totalTime: ''
        });
        csvExporter.generateCsv([emptyItem]);
      }
      return;
    } else {
      return Promise.reject('Not authorized');
    }
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/app-client-store.ts